import React, { useState } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const AddExpenseCategory = () => {
  const [formData, setFormData] = useState({ name: '', description: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((previous) => ({
      ...previous,
      [name]: value
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!formData.name.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Missing name',
        text: 'Category name is required.'
      })
      return
    }

    try {
      setSubmitting(true)
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || ''
      }
      await apiClient.post(API_ENDPOINTS.EXPENSE_CATEGORIES, payload)
      Swal.fire({
        icon: 'success',
        title: 'Category created',
        text: 'Expense category created successfully.'
      })
      setFormData({ name: '', description: '' })
    } catch (error) {
      console.error('Failed to create expense category:', error)
      const message =
        error.response?.data?.detail ||
        error.response?.data?.name?.[0] ||
        error.response?.data?.description?.[0] ||
        'Unable to create expense category. Please try again.'
      Swal.fire({
        icon: 'error',
        title: 'Creation failed',
        text: message
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Add Expense Category</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item">Expenses</li>
            <li className="breadcrumb-item active">Add Expense Category</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-0">New Category</h5>
                    <small className="text-muted">Create a reusable tag for future expenses.</small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setFormData({ name: '', description: '' })}
                    disabled={submitting}
                  >
                    Reset
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Category Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder="e.g. Utilities, Marketing"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      name="description"
                      placeholder="Optional notes to help colleagues understand when to use this category."
                      value={formData.description}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                  </div>
                  <div className="col-12 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check2-circle me-2" />
                          Save Category
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => setFormData({ name: '', description: '' })}
                      disabled={submitting}
                    >
                      <i className="bi bi-plus-circle me-2" />
                      Add Another
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="text-uppercase text-muted fw-semibold mb-3">Tips</h6>
                <ul className="list-unstyled mb-0 small text-muted d-flex flex-column gap-2">
                  <li><i className="bi bi-lightbulb me-2 text-warning" />Use clear names so everyone categorises expenses consistently.</li>
                  <li><i className="bi bi-collection me-2 text-primary" />Categories power reports like Profit/Loss and Expense breakdowns.</li>
                  <li><i className="bi bi-shield-check me-2 text-success" />Only managers and finance admins should change categories.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AddExpenseCategory
